'use client';

import { Alert, Button } from '@magicschool/spellbook';
import { useState } from 'react';
import { exitImpersonation } from '@/features/auth/impersonation';
import { getCookieValue } from '@/util/cookies';
import { DEMO_ORG_ACCESS_COOKIE, decodeDemoOrgAccessCookieValue } from '@/util/demoOrgSession';
import { IMPERSONATING_COOKIE, isImpersonating } from '@/util/impersonation';
import DemoOrgTitle from './DemoOrgTitle';

const ImpersonationBanner = () => {
  const impersonationMode = isImpersonating(getCookieValue(IMPERSONATING_COOKIE));
  const demoOrgAccess = decodeDemoOrgAccessCookieValue(getCookieValue(DEMO_ORG_ACCESS_COOKIE));
  const [isExiting, setIsExiting] = useState(false);

  const handleExitImpersonation = async () => {
    setIsExiting(true);
    try {
      const { restored, returnUrl } = await exitImpersonation();
      if (restored) {
        // Hard reload (not router.push): a soft nav can race Zustand's stale cached
        // userId against AuthSessionSync's session-mismatch check and force a sign-out.
        window.location.href = returnUrl ?? '/tools';
        return;
      }
      window.location.href = '/auth/signin';
    } finally {
      setIsExiting(false);
    }
  };

  if (!impersonationMode) {
    return null;
  }

  const isDemoPreview = demoOrgAccess?.mode === 'demo';
  const demoCopy = isDemoPreview
    ? 'Temporary preview. This account and anything tied to it will be removed on exit.'
    : 'Configure mode. Changes you make will be saved.';
  const contentText = demoOrgAccess ? demoCopy : 'You are viewing the app as another user.';

  return (
    // Announced by the PageBannerRegion this banner renders in.
    <Alert role="presentation" status="warning" variant="banner">
      <Alert.Title>
        {demoOrgAccess ? <DemoOrgTitle orgName={demoOrgAccess.orgName} role={demoOrgAccess.role} /> : 'Impersonation Mode'}
      </Alert.Title>
      <Alert.Content className="hidden sm:block">{contentText}</Alert.Content>
      <Alert.Actions>
        <Button disabled={isExiting} onClick={handleExitImpersonation} size="sm" variant="solid">
          {isExiting ? 'Exiting…' : isDemoPreview ? 'Exit demo' : 'Exit impersonation'}
        </Button>
      </Alert.Actions>
    </Alert>
  );
};

export default ImpersonationBanner;
